import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { formatPrice, getPropertyArea, getPropertyState } from '../propertyUtils';
import { useTheme } from '../context/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import './PropertyGoogleMap.css';

const GOOGLE_MAPS_LIBRARIES = [];
const GOOGLE_MAPS_SCRIPT_ID = 'naijanest-google-maps-script';

const fallbackCenter = { lat: 9.082, lng: 8.6753 };
const nigeriaBounds = {
  minLat: 4,
  maxLat: 14.5,
  minLng: 2.5,
  maxLng: 15,
};

// Hand-tuned against this app's actual dark surface tones (see
// client/src/styles/global.css's dark :root block, --bg/--surface) rather
// than an arbitrary off-the-shelf dark style, so the map doesn't read as a
// mismatched dark theme dropped into an otherwise-coordinated dark UI.
const GOOGLE_MAPS_DARK_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a94a6' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ color: '#2b3441' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#162030' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7a8699' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#14261c' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#1b2330' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#0b1220' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7a8699' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#243042' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#162030' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0b1220' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#3b4656' }],
  },
];

function isFiniteCoordinate(value) {
  return Number.isFinite(Number(value));
}

function isWithinNigeriaBounds(lat, lng) {
  return (
    lat >= nigeriaBounds.minLat &&
    lat <= nigeriaBounds.maxLat &&
    lng >= nigeriaBounds.minLng &&
    lng <= nigeriaBounds.maxLng
  );
}

function normalizePropertyCoordinates(item) {
  const lat = Number(item?.latitude);
  const lng = Number(item?.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  if (isWithinNigeriaBounds(lat, lng)) {
    return { latitude: lat, longitude: lng };
  }

  if (isWithinNigeriaBounds(lng, lat)) {
    return { latitude: lng, longitude: lat };
  }

  return null;
}

// Fits/centers the map on whatever the current property set is - called
// both on initial map load AND whenever the property set changes (e.g. a
// Browse filter narrows the results), since Google Maps only fires its own
// onLoad callback once per mount and won't otherwise re-fit when props
// change on an already-mounted map.
function fitMapToProperties(map, mapProperties) {
  if (!mapProperties.length) {
    map.setCenter(fallbackCenter);
    map.setZoom(6);
    return;
  }

  if (mapProperties.length === 1) {
    map.setCenter({ lat: mapProperties[0].latitude, lng: mapProperties[0].longitude });
    map.setZoom(15);
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  mapProperties.forEach((item) => {
    bounds.extend({ lat: item.latitude, lng: item.longitude });
  });
  map.fitBounds(bounds, 40);
}

function MapTypeToggle({ mapType, onChange }) {
  return (
    <div className="property-google-map__type-toggle" role="group" aria-label="Map type">
      <button
        type="button"
        className={mapType === 'roadmap' ? 'is-active' : ''}
        onClick={() => onChange('roadmap')}
      >
        Map
      </button>
      <button
        type="button"
        className={mapType === 'satellite' ? 'is-active' : ''}
        onClick={() => onChange('satellite')}
      >
        Satellite
      </button>
    </div>
  );
}

function PropertyGoogleMap({ properties = [], className = '', height, variant = 'browse' }) {
  const { isDarkMode } = useTheme();
  const currencyCtx = useCurrency();
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // A ref for the map instance doesn't work here: onLoad sets it
  // imperatively, which doesn't trigger React to re-run the effects below
  // that need to react to the map actually becoming available. State does.
  const [map, setMap] = useState(null);
  const clustererRef = useRef(null);
  const [mapType, setMapType] = useState('roadmap');
  const [activeMarkerId, setActiveMarkerId] = useState(null);

  const mapProperties = useMemo(() => {
    return properties
      .map((item) => {
        if (!isFiniteCoordinate(item.latitude) || !isFiniteCoordinate(item.longitude)) {
          return null;
        }
        const normalized = normalizePropertyCoordinates(item);
        if (!normalized) {
          return null;
        }
        return { ...item, latitude: normalized.latitude, longitude: normalized.longitude };
      })
      .filter(Boolean);
  }, [properties]);

  const activeMarkerProperty = mapProperties.find((item) => item.id === activeMarkerId) || null;

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Fits/re-fits whenever the map becomes available OR the (already
  // normalized/filtered) property set changes on an already-mounted map -
  // e.g. a Browse filter narrowing the results - since Google Maps' own
  // onLoad callback only fires once per mount and won't otherwise re-fit
  // when props change.
  useEffect(() => {
    if (map) {
      fitMapToProperties(map, mapProperties);
    }
  }, [map, mapProperties]);

  // Markers are created imperatively (not as declarative <Marker> children)
  // because MarkerClusterer needs real google.maps.Marker instances to
  // group - properties that share a fallback-resolved location (see
  // server/geocode.js's resolveLocation cascade) would otherwise render as
  // perfectly overlapping, indistinguishable pins.
  useEffect(() => {
    if (!map || !window.google) {
      return undefined;
    }

    const markers = mapProperties.map((item) => {
      const marker = new window.google.maps.Marker({
        position: { lat: item.latitude, lng: item.longitude },
      });
      marker.addListener('click', () => setActiveMarkerId(item.id));
      return marker;
    });

    clustererRef.current = new MarkerClusterer({ map, markers });

    return () => {
      clustererRef.current?.clearMarkers();
      clustererRef.current?.setMap(null);
      markers.forEach((marker) => marker.setMap(null));
    };
  }, [map, mapProperties]);

  const mapOptions = useMemo(
    () => ({
      mapTypeId: mapType,
      styles: isDarkMode && mapType === 'roadmap' ? GOOGLE_MAPS_DARK_STYLE : [],
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      keyboardShortcuts: false,
      // 'greedy': plain mouse wheel zooms and one-finger drag pans directly,
      // no Ctrl+scroll / two-finger requirement - matches how every other
      // map a user has used behaves, on both the Browse map/split views and
      // the detail page's fullscreen Map tab.
      gestureHandling: 'greedy',
    }),
    [mapType, isDarkMode]
  );

  if (!mapProperties.length) {
    // Skip mounting a map that would just sit at its fallback, zoomed-out
    // view with nothing on it - that reads as broken, not as "no results".
    return (
      <div className={`property-map-shell ${className}`.trim()}>
        <div className="map-empty-state map-empty-state--standalone">
          {variant === 'detail' ? (
            <>
              <p>Map location not available for this property.</p>
              <small>The listing's address hasn't been pinned to a location yet.</small>
            </>
          ) : (
            <>
              <p>No map-ready properties found for the current filter.</p>
              <small>Try broadening filters or adding listings with valid locations.</small>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY || !isLoaded) {
    return (
      <div className={`property-map-shell ${className}`.trim()} style={height ? { height } : undefined} />
    );
  }

  return (
    <div className={`property-map-shell ${className}`.trim()}>
      <GoogleMap
        mapContainerClassName="property-map"
        mapContainerStyle={height ? { height } : undefined}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={mapOptions}
      >
        <MapTypeToggle mapType={mapType} onChange={setMapType} />

        {activeMarkerProperty && (
          <InfoWindow
            position={{ lat: activeMarkerProperty.latitude, lng: activeMarkerProperty.longitude }}
            onCloseClick={() => setActiveMarkerId(null)}
          >
            <div className="map-popup-card">
              <strong>{activeMarkerProperty.title}</strong>
              <p>{formatPrice(activeMarkerProperty.price, currencyCtx)}</p>
              <small>
                {getPropertyArea(activeMarkerProperty) || 'Unknown area'},{' '}
                {getPropertyState(activeMarkerProperty) || 'Nigeria'}
              </small>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

export default PropertyGoogleMap;
